import React, { useCallback } from "react";
import { useForm } from "react-hook-form-7";
import useNotification from "../../hooks/useNotification";
import useLocalizedOptionsConfiguration from "../../hooks/useLocalizedOptionsConfiguration";
import useTranslations from "../../hooks/useTranslations";
import useViewer from "../../hooks/useViewer";

import {
  Button,
  Modal,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  HStack,
  Spacer,
  VStack,
  RadioGroup,
  Radio,
  FormControl,
  FormLabel,
} from '@chakra-ui/react';
import { QuestionFragment, useInsertAnswer } from '../../graphql/generated/definitions';

type GradeUserSkillModalProps = {
  open: boolean;
  question: QuestionFragment;
  onClose: VoidFunction;
  userId: string;
  refetch;
};

const GradeUserSkillModal: React.FC<GradeUserSkillModalProps> = ({
  open,
  question,
  onClose,
  userId,
  refetch,
}) => {
  const t = useTranslations();
  const { id: viewerId } = useViewer();
  const options = useLocalizedOptionsConfiguration();
  const { register, handleSubmit } = useForm({});

  const { notifySuccess, notifyError } = useNotification();

  const [insertAnswer] = useInsertAnswer();

  const onSubmit = useCallback(
    async (data) => {
      try {
        await insertAnswer({
          variables: {
            input: {
              // FIXME
              id: data.id,
              questionId: data.questionId,
              userId: viewerId,
              collaboratorId: userId,
              value: data.value,
              type: "AnswerSkill",
            },
          },
        });
        notifySuccess({
          title: t("users.skills.notification.success.grade.title") as string,
          description: t("users.skills.notification.success.grade.body") as string,
        });
      } catch {
        notifyError({
          title: t("users.skills.notification.error.title") as string,
        });
      }
      onClose();
      refetch();
    },
    [insertAnswer]
  );

  return (
    <Modal
      isOpen={open}
      onClose={onClose}
    >
      <ModalOverlay />
      <ModalContent>
        <form onSubmit={handleSubmit(onSubmit)} id="questionForm">
            <ModalHeader>
              {t("users.skills.grade")}
            </ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <input
                type="hidden"
                {...register("questionId")}
                defaultValue={question.id}
              />
              <FormControl>
                <FormLabel>
                  {t("users.skills.selectGrade")}
                </FormLabel>
                <RadioGroup>
                  <VStack align="stretch">
                    {options.skillQuestionOptionsConfiguration.map(({ value, label }) => (
                      <React.Fragment key={value}>
                        <Radio
                          value={value}
                          data-test-id={`radio_selectGrade_${value}`}
                          {...register(`${name}.value.selector`)}
                        >
                          {label}
                        </Radio>
                        <Spacer />
                      </React.Fragment>
                    ))}
                  </VStack>
                </RadioGroup>
              </FormControl>
            </ModalBody>
            <ModalFooter>
              <HStack>
                <Button
                  onClick={onClose}
                >
                  {t("forms.actions.cancel")}
                </Button>

                <Spacer />

                <Button
                  type="submit"
                  form="questionForm"
                  colorScheme="blue"
                >
                  {t("forms.actions.save")}
                </Button>
              </HStack>
            </ModalFooter>
          </form>
      </ModalContent>
    </Modal>
  );
};

export default React.memo(GradeUserSkillModal);
